import React, { useEffect, useRef, useState } from 'react';
import './PropertyTypes.css';
import './PropertyTypes.mobile.css';
import './Explore360.css';
import PopupQuiz from './PopupQuiz';

const PropertyTypes = () => {
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Plot positions organized by rows - matching exact SVG coordinates from 1000x700 canvas
  const plotPositions = [
    { id: 1, x: 125, y: 21.8, width: 147, height: 203, title: "Plot 1", row: 1, depth: 0.4 },
    { id: 2, x: 288, y: 90.2, width: 148, height: 207.2, title: "Plot 2", row: 1, depth: 0.6 },
    { id: 3, x: 452, y: 191.4, width: 149, height: 130.99999999999997, title: "Plot 3", row: 1, depth: 0.8 },
    { id: 4, x: 624, y: 90.4, width: 146, height: 203.99999999999997, title: "Plot 4", row: 1, depth: 0.5 },
    { id: 5, x: 789, y: 21.2, width: 151, height: 205, title: "Plot 5", row: 1, depth: 0.3 },
    { id: 6, x: 125, y: 260, width: 148, height: 127, title: "Plot 6", row: 2, depth: 0.7 },
    { id: 7, x: 289, y: 328, width: 151, height: 131, title: "Plot 7", row: 2, depth: 0.9 },
    { id: 8, x: 450, y: 330.2, width: 151, height: 131, title: "Plot 8", row: 2, depth: 1.0 },
    { id: 9, x: 623, y: 328.8, width: 151, height: 130, title: "Plot 9", row: 2, depth: 0.85 },
    { id: 10, x: 791, y: 260.8, width: 148, height: 129, title: "Plot 10", row: 2, depth: 0.65 },
    { id: 11, x: 125, y: 421, width: 146, height: 204, title: "Plot 11", row: 3, depth: 0.55 },
    { id: 12, x: 290, y: 489.8, width: 146, height: 202.99999999999994, title: "Plot 12", row: 3, depth: 0.75 },
    { id: 13, x: 452, y: 488.2, width: 148, height: 209.00000000000006, title: "Plot 13", row: 3, depth: 0.95 },
    { id: 14, x: 622, y: 489.2, width: 151, height: 208.00000000000006, title: "Plot 14", row: 3, depth: 0.8 },
    { id: 15, x: 791, y: 420.4, width: 149, height: 205, title: "Plot 15", row: 3, depth: 0.6 }
  ];

  const propertyImages = [
    "/12 villa image/villa 1.png",
    "/12 villa image/villa 2.png",
    "/12 villa image/villa 3.png",
    "/12 villa image/villa 4.png",
    "/12 villa image/villa 5.png",
    "/12 villa image/villa 6.png",
    "/12 villa image/villa 7.png",
    "/12 villa image/villa 8.png",
    "/12 villa image/villa 9.png",
    "/12 villa image/3713-202310111333337936.jpg",
    "/12 villa image/Glamping-dome-6m-size-4.jpg",
    "/12 villa image/villa 1.png",
    "/12 villa image/villa 2.png",
    "/12 villa image/villa 3.png",
    "/12 villa image/villa 4.png"
  ];



  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!wrapperRef.current) return;

      const rect = wrapperRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const wrapperHeight = wrapperRef.current.offsetHeight;
      const wrapperTop = wrapperRef.current.offsetTop;
      const currentScroll = window.scrollY;
      
      // Calculate scroll progress within the wrapper
      // Start counting when wrapper top reaches viewport top (sticky point)
      let progress = 0;
      
      // Check if we've reached the sticky point (when wrapper top would be at viewport top)
      if (currentScroll >= wrapperTop) {
        // Calculate how much we've scrolled past the sticky point
        const scrolled = currentScroll - wrapperTop;
        // Total scrollable is wrapper height minus one viewport height (since section is sticky)
        const totalScrollable = wrapperHeight - windowHeight;
        
        if (totalScrollable > 0) {
          progress = Math.max(0, Math.min(1, scrolled / totalScrollable));
        } else {
          progress = 1;
        }
      } else {
        progress = 0;
      }

      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    handleScroll(); // Initial call

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  const getCardStyles = (plot) => {
    const windowHeight = window.innerHeight || 800;
    
    // Calculate upward movement based on scroll progress
    // Adjust to end when last row (row 3) cards reach the top
    const maxUpwardMovement = windowHeight * 1.5;
    const upwardMovement = scrollProgress * maxUpwardMovement;
    
    // Get container position to calculate card's absolute position
    let cardRelativeTop = 0;
    let cardHeightPx = 0;
    if (containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const cardTopPercent = (plot.y / 700) * 100;
      cardRelativeTop = (cardTopPercent / 100) * containerRect.height;
      
      // Calculate card height in pixels
      const cardHeightPercent = (plot.height / 700) * 100;
      cardHeightPx = (cardHeightPercent / 100) * containerRect.height;
    }
    
    // All cards have 100% height
    const heightScale = 1.0;
    const cardFinalHeightPx = cardHeightPx * heightScale;
    
    // Calculate where the card's top and bottom will be after translation
    const cardFinalTop = containerRef.current ? 
      containerRef.current.getBoundingClientRect().top + cardRelativeTop - upwardMovement : 0;
    const cardFinalBottom = cardFinalTop + cardFinalHeightPx;
    
    // Determine opacity - fade out cards only when their bottom edge goes above viewport
    let opacity = 1;
    if (cardFinalBottom < -50) {
      // Card's bottom is completely above viewport - fully hidden
      opacity = 0;
    } else if (cardFinalBottom < 0) {
      // Card's bottom is partially above viewport - fade it out
      opacity = Math.max(0, (cardFinalBottom + 50) / 50);
    }
    
    // Determine transform and z-index
    let transform = '';
    let zIndex = 1;

    // Apply upward translation - cards scroll up naturally
    transform = transform ? `${transform} translateY(-${upwardMovement}px)` : `translateY(-${upwardMovement}px)`;

    // Adjust layout with minimal gaps
    // Scale the coordinate system minimally to create small vertical space
    const layoutScale = 1.0; // No scaling to minimize gaps
    const originalTop = (plot.y / 700) * 100;
    
    // Add very minimal spacing between rows to prevent overlap
    // Cards in different rows get tiny spacing
    let rowSpacing = 0;
    if (plot.row === 2) {
      rowSpacing = 0.25; // Minimal gap for row 2
    } else if (plot.row === 3) {
      rowSpacing = 0.5; // Minimal gap for row 3
    }
    
    // Scale the top position and add row spacing
    const adjustedTop = (originalTop * layoutScale) + rowSpacing;
    
    // Adjust left position slightly to maintain horizontal alignment
    const adjustedLeft = (plot.x / 1000) * 100;
  // Global horizontal offset to nudge all cards horizontally (positive moves right, negative moves left)
  const globalHorizontalOffset = -2; // percent — reduced left shift so cards move slightly right

    // Apply offset and clamp between -10% and 110% to avoid extreme positions
    const leftWithOffset = Math.min(110, Math.max(-10, adjustedLeft + globalHorizontalOffset));

    return {
      left: `${leftWithOffset}%`,
      top: `${Math.max(0, adjustedTop)}%`,
      width: `${(plot.width / 1000) * 100}%`,
      height: `calc(${(plot.height / 700) * 100}% * ${heightScale})`,
      transform: transform,
      opacity: opacity,
      zIndex: zIndex
    };
  };

  // Calculate blur amount based on scroll progress
  const blurAmount = Math.min(scrollProgress * 10, 8);

  const [isQuizOpen, setIsQuizOpen] = useState(false);

  const openQuiz = () => setIsQuizOpen(true);
  const closeQuiz = () => setIsQuizOpen(false);

  // Disable blur on mobile devices
  const appliedBlur = isMobile ? 0 : blurAmount;

  const renderMobileLayout = () => (
    <div className="mobile-grid">
      {propertyImages.map((img, index) => (
        <div className="mobile-item" key={`mobile-${index}`}>
          <img src={img} alt={`Property ${index + 1}`} />
        </div>
      ))}
    </div>
  );

  const renderDesktopLayout = () => (
    <div className="property-layout" ref={containerRef}>
      {plotPositions.map((plot, index) => {
        const styles = getCardStyles(plot);
        return (
          <div
            key={plot.id}
            className="property-plot"
            style={styles}
            title={plot.title}
          >
            <img
              src={propertyImages[index]}
              alt={plot.title}
              className="plot-image"
            />
          </div>
        );
      })}
      <div
        className="quiz-plot"
        style={{
          ...getCardStyles({ x: 200, y: 760, width: 600, height: 170, row: 4 }),
          backgroundImage: "url('/jungle.jpg')"
        }}
      >
        <div className="quiz-plot-content">
          <div className="quiz-plot-copy">
            <h3 className="quiz-plot-heading">Find your ideal home in 1 Minute</h3>
            <p className="quiz-plot-sub">Sit back and relax. Let our team do the work.</p>
          </div>
          <button className="quiz-plot-button" onClick={openQuiz}>Take the quiz</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="property-types-wrapper" ref={wrapperRef}>
      <section id="properties" className="property-types section">
        <div className="container">
          <h2 
            className="section-title" 
            style={{ filter: `blur(${appliedBlur}px)`, transition: 'filter 0.1s ease-out', color: 'rgba(0,0,0,0.7)' }}
          >
            CRAFT YOUR SANCTUARY
          </h2>
          <p 
            className="section-subtitle" 
            style={{ filter: `blur(${appliedBlur}px)`, transition: 'filter 0.1s ease-out' }}
          >
            EACH FORM — FROM VILLAS TO DOMES — REFLECTS A QUIET<br />DIALOGUE BETWEEN DESIGN, CRAFT, AND NATURE.
          </p>

          {isMobile ? renderMobileLayout() : renderDesktopLayout()}

          {/* On mobile show a CTA to open the quiz (and render inline when opened) */}
          {isMobile && !isQuizOpen && (
            <div className="mobile-quiz-cta" style={{ marginTop: 12 }}>
              <div className="mobile-quiz-copy">Find your ideal home in 1 Minute</div>
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
                <button className="quiz-plot-button" onClick={openQuiz}>Take the quiz</button>
              </div>
            </div>
          )}

          {isQuizOpen && <PopupQuiz onClose={closeQuiz} />}
        </div>
      </section>
    </div>
  );
};

export default PropertyTypes;
